/**
 * Observer bootstrap - slim entry point for the professional read-only observer build.
 *
 * This file is the observer equivalent of main.ts. It registers only the plugins
 * needed for read-only observation. Everything parent-specific is excluded:
 * - No VitePWA / Service Worker
 * - No Raygun with parent user context
 * - No AppInsights with parent connection string
 * - No Rewardful referral plugin
 * - No form validation rules (no write forms in observer)
 * - No parent stores (LoginStore, CommonStore, MessagesStore, etc.)
 * - No parent httpClient with token refresh interceptors
 */

import { createApp } from 'vue'
import { watch } from 'vue'
import { createPinia, storeToRefs } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import { PiniaColada } from '@pinia/colada'

import ObserverApp from './ObserverApp.vue'
import router from './router/observerRoutes'

import { useProfessionalSessionStore } from './stores/ProfessionalSessionStore'
// Same module the observer build serves for '@/stores/CommonStore' (see
// vite.config.observer.ts alias); imported directly so type checking sees
// the observer store's shape.
import { useCommonStore } from './stores/ObserverCommonStore'

import VueDOMPurifyHTML from 'vue-dompurify-html'
import DOMPurify from 'dompurify'

import { Settings } from 'luxon'

import i18n from './i18n'
import { ensureObserverLocaleReady } from './pages/observer/services/observerLocale'

// FontAwesome
import { library, type IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { all } from '@talkingparents/kit-4ad9ec01d5/icons'

library.add(...(all as IconDefinition[]))

// Luxon defaults
Settings.throwOnInvalid = true
Settings.defaultWeekSettings = { firstDay: 7, minimalDays: 1, weekend: [6, 7] }

// Create app
const app = createApp(ObserverApp)

// Pinia (with persisted state for session store)
const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)
app.use(pinia)
// Same query cache settings as main.ts so shared repositories behave alike.
app.use(PiniaColada, {
  queryOptions: {
    staleTime:
      import.meta.env?.MODE === 'production' ? 5 * 60 * 1000 : 1 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  }
})

// Keep the aliased CommonStore identity in sync with the observed client so
// shared components resolve the account owner and coparent like the consumer app.
const observerCommonStore = useCommonStore(pinia)
const { windowWidth } = storeToRefs(observerCommonStore)
const { setObservedIdentity, applyFullUserInfo } = observerCommonStore

const professionalSessionStore = useProfessionalSessionStore(pinia)
const {
  observedParentUserId,
  observedParentName,
  observedCoparentName,
  isProfessionalSession,
  clientInfo
} = storeToRefs(professionalSessionStore)
const { fetchClientInfo } = professionalSessionStore

// web_spanish_version is a fixed boolean (see ObserverCommonStore.isProd) -
// locale resolves in the background so mount isn't blocked.
void ensureObserverLocaleReady()

// Calling query keys include this identity, so a client switch cannot reuse
// another client's history/details while the new request is in flight.
watch(
  [observedParentUserId, observedParentName, observedCoparentName],
  ([observedUserId, observedName, observedCoparent]) => {
    const parsedUserId = Number(observedUserId)

    setObservedIdentity({
      userId:
        Number.isFinite(parsedUserId) && parsedUserId > 0 ? parsedUserId : null,
      fullName: observedName ?? '',
      coparentFullName: observedCoparent ?? ''
    })
  },
  { immediate: true, flush: 'sync' }
)

// Hydrate the observed parent's real account settings (timezone, date format)
// once per page load so timestamps render in the parent's chosen timezone,
// never the professional's local one. The session store fetches the ClientInfo
// projection (GET /api/professional/readOnly/GetClientInfo) and caches it in
// clientInfo; this watch merges it into the aliased CommonStore. Failures keep
// the store defaults.
watch(
  [isProfessionalSession, observedParentUserId],
  ([isSessionActive, observedUserId]) => {
    if (!isSessionActive || !observedUserId) return
    // Deduped in the session store - safe alongside the router guard's call
    fetchClientInfo()
  },
  { immediate: true }
)

watch(
  clientInfo,
  (info) => {
    if (info) {
      applyFullUserInfo(info)
    }
  },
  { immediate: true }
)

// Router
app.use(router)

// i18n
app.use(i18n)

// DOMPurify
app.use(VueDOMPurifyHTML, { default: { ALLOWED_TAGS: ['em'] } })

// Toastify (for observer-safe notifications like "Session extended")
import Vue3Toastify, { toast, type ToastContainerOptions } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'

app.use(Vue3Toastify, {
  autoClose: 3000,
  closeButton: false,
  position: toast.POSITION.TOP_CENTER,
  icon: false,
  transition: { enter: 'toast-enter', exit: 'toast-exit', collapse: false },
  clearOnUrlChange: false
} as ToastContainerOptions)

// Shared directives (read-only safe)
import { ObserveVisibility } from 'vue-observe-visibility'

app.directive('observe-visibility', {
  beforeMount: (el, binding, vnode) => {
    ;(vnode as typeof vnode & { context?: unknown }).context = binding.instance
    ObserveVisibility.bind(el, binding, vnode)
  },
  updated: ObserveVisibility.update,
  unmounted: ObserveVisibility.unbind
})

app.directive('skeleton', (el, binding) => {
  const value = binding.value

  if (value == null) {
    el.classList.remove('skeleton')
    return
  }

  const isBoolean = typeof value === 'boolean'
  const isObj = typeof value === 'object'

  const loading = isBoolean
    ? value
    : isObj && 'loading' in value && !!value.loading

  const minWidth = isObj && 'minWidth' in value ? Number(value.minWidth) : 4.5

  const maxWidth = isObj && 'maxWidth' in value ? Number(value.maxWidth) : 10

  if (loading) {
    el.classList.add('skeleton')

    if (!el.style.getPropertyValue('--skeleton-width').length) {
      const finalWidth = Math.random() * (maxWidth - minWidth) + minWidth
      el.style.setProperty('--skeleton-width', `${finalWidth}rem`)
    }
  } else {
    el.classList.remove('skeleton')
  }
})

app.directive('inline', (element) => {
  element.replaceWith(...element.children)
})

app.directive('highlight-text', (el, binding) => {
  const searchTerm = binding.value.searchTerm as string | string[]
  const _class = binding.value.class
  const _style = binding.value.style
  const loading = binding.value.loading
  let text = el.innerHTML as string

  if (loading) {
    return
  }

  // 1. sanitize text
  text = DOMPurify.sanitize(text, {
    ALLOWED_TAGS: ['em', 'a'],
    ALLOWED_ATTR: ['href', 'target']
  })

  if (searchTerm && text) {
    let regex = null as RegExp | null

    if (Array.isArray(searchTerm) && searchTerm.length) {
      let regexString = escapeRegExp(searchTerm[0])

      for (const term of searchTerm.slice(1)) {
        regexString = `${regexString}|${escapeRegExp(term)}` // 2. for each search term, add to regex
      }

      regex = new RegExp(regexString, 'gi')
    } else if (!Array.isArray(searchTerm) && searchTerm.length) {
      regex = new RegExp(escapeRegExp(searchTerm), 'gi') // 2. add singlular term to regex
    }

    const replacement = `<span class="${_class}" style="${_style}">$&</span>`

    el.innerHTML = regex ? text.replace(regex, replacement) : text // 3. replace search term match with highlight style
  } else if (!searchTerm && text) {
    el.innerHTML = text
  }
})

// Global components
app.component('FontAwesomeIcon', FontAwesomeIcon)

import loading from '@/components/library/Loading.vue'
app.component('loading', loading)

import { VueDatePicker } from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
app.component('Datepicker', VueDatePicker)

// V3 Scroll Lock
import V3ScrollLock from 'v3-scroll-lock'
import { escapeRegExp } from './utils/regex.utils.ts'
app.use(V3ScrollLock, {})

// Mount immediately - locale resolves in the background above, it never
// blocks first paint.
// Keep windowWidth in sync for responsive breakpoints
window.addEventListener('resize', () => {
  windowWidth.value = window.innerWidth
})

app.mount('#app')
